import React, { Suspense, lazy, useEffect, useRef } from "react";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { useWebChatContext } from "./WebChatContext";
import { useWebChatStyles } from "./WebChatStyles";
import { useWebChatTelemetry } from "./useWebChatTelemetry";

const WebChat = lazy(() => import("./WebChat"));

/**
 * Lightweight panel shown while the heavy WebChat chunk is being fetched.
 * Composes existing WebChatStyles so the loading shell matches the eventual
 * chat panel (same width/positioning/header) — the only thing that changes
 * is the body content. Keeps a working Close button so users can back out
 * during a slow cellular fetch.
 *
 * Inherits ALL responsive behaviour (mobile fullscreen, tablet fluid panel,
 * desktop 400px) from `styles.chatWindow` automatically — there is no
 * separate mobile path to maintain here.
 *
 * Exported for direct testing; the Suspense fallback wires it up below.
 */
export const ChatLoadingPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const styles = useWebChatStyles();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  // Move keyboard focus to the close button on mount so screen-reader users
  // know how to back out of a slow connection without having to tab past
  // the loading text. Visible focus ring on a real button.
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);
  return (
    <div
      className={styles.chatWindow}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dining-assistant-title"
      data-testid="webchat-window"
    >
      <div className={styles.header}>
        <span id="dining-assistant-title" className={styles.headerTitle}>
          Dining Assistant
        </span>
        <button
          ref={closeButtonRef}
          className={styles.headerCloseButton}
          onClick={onClose}
          aria-label="Close chat"
        >
          <Dismiss16Regular />
        </button>
      </div>
      <div className={styles.stateContainer} role="status" aria-live="polite">
        <p>Loading Dining Assistant…</p>
      </div>
    </div>
  );
};

/**
 * Eagerly-loaded wrapper around the heavy WebChat component.
 *
 * Responsibilities:
 *   - Keep `botframework-webchat` + adapters out of the initial JS bundle via
 *     `React.lazy` + Suspense; the chunk is only fetched when the user opens
 *     the chat for the first time in a session.
 *   - Fire `opened` / `closed` telemetry on isOpen transitions even when the
 *     lazy-loaded WebChat is unmounted (the default state on non-chat usage).
 *   - Mount the chat panel only while open, so non-chat routes (every route
 *     in the app today) pay zero render or DirectLine cost for the chat
 *     surface.
 *   - Show a loading shell (header + spinner-style status message + close
 *     button) while the lazy chunk downloads, so users on slow connections
 *     see something rather than a blank gap.
 */
export const LazyWebChat: React.FC = () => {
  const styles = useWebChatStyles();
  const { isOpen, setIsOpen, openerRef } = useWebChatContext();
  const telemetry = useWebChatTelemetry();
  const previousOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = isOpen;

    if (!wasOpen && isOpen) {
      // false → true : opening. Opener was captured synchronously inside
      // setIsOpen() — see WebChatContext.
      telemetry.opened();
    } else if (wasOpen && !isOpen) {
      // true → false : closing
      telemetry.closed();
      const saved = openerRef.current;
      openerRef.current = null;
      try {
        if (saved && document.contains(saved)) {
          saved.focus();
        } else {
          // Saved element was unmounted (the toggle button returns null
          // while isOpen=true and re-mounts on close as a fresh DOM node).
          // Focus the freshly-mounted launcher by its aria-label.
          const launcher = document.querySelector<HTMLElement>('[aria-label="Open chat"]');
          launcher?.focus?.();
        }
      } catch {
        /* noop — element no longer focusable */
      }
    }
  }, [isOpen, telemetry, openerRef]);

  // Escape-to-close — only bound while the chat is open. Intentionally
  // skipped on mobile (<500px) where the chat panel IS the surface and
  // there is no underlying app to dismiss to (Apple HIG / Material both
  // recommend a tappable close button on fullscreen sheets, which we
  // already provide via the header).
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // matchMedia is a no-op in jsdom — guarded for SSR / test envs too.
      if (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 499px)").matches
      ) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setIsOpen]);

  if (!isOpen) return null;
  return (
    <>
      {/* Backdrop scrim — visible only on tablet (500–1239) via CSS media
          query. Click-to-close. Mobile and desktop never paint it (the
          rule sets display:none outside that range). */}
      <div
        className={styles.backdrop}
        aria-hidden="true"
        data-testid="webchat-backdrop"
        onClick={() => setIsOpen(false)}
      />
      <Suspense fallback={<ChatLoadingPanel onClose={() => setIsOpen(false)} />}>
        <WebChat />
      </Suspense>
    </>
  );
};

export default LazyWebChat;
